import { ConfigCatKeys, FeatureConfigKeys } from "app/common/constant/featureConfig";
import { useRouter } from "next/router";
import { useCallback } from "react";

/**
 * Custom hook to handle feature overrides from query parameters and session storage.
 *
 * This hook is responsible for fetching feature override configurations either from:
 * - The query parameter `featureOverride` in the URL (if available), or
 * - The session storage (if available), and filters the overrides based on the allowed keys passed to the `getFeatureOverrides` function.
 *
 * `getFeatureOverrides` should only be used when `isRouterReady` is true
 */
export const useFeatureOverride = (): {
	isRouterReady: boolean;
	getFeatureOverrides: (allowedKeys: FeatureConfigKeys[]) => Partial<ConfigCatKeys>;
} => {
	const FEATURE_OVERRIDE_KEY = "featureOverrides";
	const router = useRouter();

	const getFeatureOverridesFromQuery = useCallback((): Partial<ConfigCatKeys> | undefined => {
		try {
			if (router.query.featureOverride) {
				const decoded = decodeURIComponent(router.query.featureOverride as string);

				const json = JSON.parse(decoded) as Partial<ConfigCatKeys>;

				sessionStorage.setItem(FEATURE_OVERRIDE_KEY, JSON.stringify(json));
				return json;
			}
			return;
		} catch (err) {
			// return and clear FEATURE_OVERRIDE_KEY if malformed URI component
			sessionStorage.removeItem(FEATURE_OVERRIDE_KEY);
			return {};
		}
	}, [router.query.featureOverride]);

	const getFeatureOverrides = useCallback(
		(allowedFeatureOverrides: FeatureConfigKeys[]): Partial<ConfigCatKeys> => {
			try {
				// retrieve overrides from query OR session storage
				const featureOverrides =
					getFeatureOverridesFromQuery() ||
					(JSON.parse(sessionStorage.getItem(FEATURE_OVERRIDE_KEY) as string) as Partial<ConfigCatKeys>);

				// only return overrides that are allowed
				const filtered: Partial<ConfigCatKeys> = Object.fromEntries(
					Object.entries(featureOverrides).filter(([key]) =>
						allowedFeatureOverrides.includes(key as FeatureConfigKeys),
					),
				);

				return filtered;
			} catch (err) {
				return {};
			}
		},
		[getFeatureOverridesFromQuery],
	);

	return { isRouterReady: router.isReady, getFeatureOverrides };
};
